
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { Producto } from '@shared/types';
import { cn } from '@/lib/utils';
import { useSortableTable } from '@/hooks/useSortableTable';
import { useTaxes } from '@/contexts/TaxContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ExportDialog } from '@/components/ExportDialog';
import { ChevronsUpDown, Download, Loader2, Pencil, Plus, Trash } from 'lucide-react';
import * as XLSX from 'xlsx';

export default function ProductosPage() {
  const { fetchWithAuth } = useUser();
  const [productos, setProductos] = useState<Producto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isExportDialog, setExportDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { getTaxById } = useTaxes();

  const loadProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth('/api/productos');
      if (!response.ok) throw new Error('No se pudieron cargar los productos');
      const data = await response.json();
      setProductos(data);
    } catch (error) {
      toast.error('Error al cargar', { description: error instanceof Error ? error.message : 'Ocurrió un error inesperado.' });
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return productos;
    return productos.filter(
      (p) =>
        p.nombre.toLowerCase().includes(q) ||
        (p.descripcion || '').toLowerCase().includes(q)
    );
  }, [productos, search]);

  const { sorted, sortBy, sortKey } = useSortableTable(filtered, 'nombre');

  const handleRemoveConfirm = () => {
    if (!deleteId) return;

    const promise = fetchWithAuth(`/api/productos/${deleteId}`, { method: 'DELETE' })
      .then(res => {
        if (!res.ok) throw new Error('No se pudo eliminar el producto');
        return res;
      });

    toast.promise(promise, {
      loading: 'Eliminando producto...',
      success: () => {
        loadProducts();
        setDeleteId(null);
        return 'Producto eliminado correctamente.';
      },
      error: (err) => `Error: ${err.message}`
    });
  };
  
  const allHeaders = ['Nombre', 'Descripción', 'Precio', 'Impuesto', 'Referencia'];

  const handleExport = (selectedHeaders: string[]) => {
    const dataToExport = sorted.map(p => {
        const row: { [key: string]: any } = {};
        selectedHeaders.forEach(header => {
            switch (header) {
                case 'Nombre': row[header] = p.nombre; break;
                case 'Descripción': row[header] = p.descripcion; break;
                case 'Precio': row[header] = p.precio; break;
                case 'Impuesto': row[header] = p.taxId ? getTaxById(p.taxId)?.nombre : ''; break;
                case 'Referencia': row[header] = p.referencia || ''; break;
            }
        });
        return row;
    });
    const ws = XLSX.utils.json_to_sheet(dataToExport, { header: selectedHeaders });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Productos");
    XLSX.writeFile(wb, "productos.xlsx");
    toast.success("Exportación completada", { description: "El fichero de productos se ha descargado." });
  }

  const SortableHeader: React.FC<{ column: keyof Producto, label: string, className?: string }> = ({ column, label, className }) => (
    <TableHead onClick={() => sortBy(column)} className={cn("cursor-pointer hover:bg-muted/50 transition-colors", className)}>
      <div className="flex items-center gap-2">
        {label}
        {sortKey === column && <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />}
      </div>
    </TableHead>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <div>
            <h1 className="text-3xl font-bold tracking-tighter">Productos y Servicios</h1>
            <p className="text-muted-foreground mt-1">Gestiona tu catálogo para facturas y presupuestos.</p>
        </div>
        <div className="flex gap-2">
            <Button variant="outline" onClick={() => setExportDialog(true)} disabled={productos.length === 0}>
                <Download className="mr-2 h-4 w-4" /> Exportar
            </Button>
            <Button onClick={() => navigate('/productos/nuevo')}>
                <Plus className="mr-2 h-4 w-4" /> Nuevo Ítem
            </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Catálogo</CardTitle>
          <CardDescription>Aquí puedes ver y gestionar todos tus productos y servicios.</CardDescription>
          <div className="mt-4">
            <Input
                placeholder="Buscar por nombre o descripción..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader column="nombre" label="Nombre" />
                  <SortableHeader column="descripcion" label="Descripción" />
                  <SortableHeader column="precio" label="Precio" className="text-right" />
                  <TableHead>Impuesto</TableHead>
                  <TableHead className="text-right pr-6">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                 {isLoading ? (
                    <TableRow>
                        <TableCell colSpan={5} className="h-24 text-center">
                            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                        </TableCell>
                    </TableRow>
                 ) : sorted.length > 0 ? sorted.map((p) => {
                  const tax = p.taxId ? getTaxById(p.taxId) : undefined;
                  return (
                    <TableRow key={p.id} onDoubleClick={() => navigate(`/productos/${p.id}/edit`)} className="hover:bg-muted/50 cursor-pointer">
                      <TableCell className="font-medium">{p.nombre}</TableCell>
                      <TableCell className="text-muted-foreground">{p.descripcion}</TableCell>
                      <TableCell className="text-right font-mono">{p.precio.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{tax ? `${tax.nombre} (${(tax.rate * 100).toFixed(0)}%)` : '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end items-center">
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/productos/${p.id}/edit`); }}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteId(p.id ?? null); }}>
                            <Trash className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                }) : (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">No has añadido ningún producto todavía.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <ExportDialog 
        open={isExportDialog} 
        onOpenChange={setExportDialog} 
        allHeaders={allHeaders}
        onExport={handleExport}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(isOpen) => !isOpen && setDeleteId(null)}>
          <AlertDialogContent>
              <AlertDialogHeader>
                  <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                  <AlertDialogDescription>Esta acción es irreversible y eliminará el producto permanentemente.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setDeleteId(null)}>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRemoveConfirm}>Confirmar</AlertDialogAction>
              </AlertDialogFooter>
          </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
